import React, { useMemo, useState } from "react";
import styled, { useTheme } from "styled-components";
import { FiInfo } from "react-icons/fi";
import { Section, Widget, WidgetGrid, tokens } from "../../activity-logs/styles";
import { IOS } from "../../activity-logs/labels";
import { Notice, PeriodStats, Tag, TopHeadline } from "../../creditos/styles";
import { formatCompact, formatCurrency, formatPercent } from "../../creditos/dashboardData";
import { hairline } from "../../../styles/designTokens";
import {
  CardRow, Compare, Figure, RateDumbbell, directionColor, signed,
} from "../dashboard/ReformaDashboard";
import { formatTimes } from "../dashboard/reformaData";
import { useReformaParams } from "../hooks/useReformaParams";
import { Control } from "../simulacao/SimulationDashboard";
import { buildCash } from "./caixaData";
import { AnimatedMoney as MoneyValue, FluidRange, Num } from "../components/Motion";
import { finePercent } from "../components/Motion";

/**
 * Aba Caixa: quanto dinheiro passa pelo caixa, quanto fica em risco e onde agir
 * primeiro. Elementos próprios: balança débito × crédito na mesma escala, teste de
 * estresse, anatomia dos serviços e plano de ação por prazo. A cor segue o significado:
 * azul é débito, verde é crédito, vermelho é o que sai de fato do caixa.
 */

const DEBIT = IOS.blue;
const CREDIT = IOS.green;
const PAY = IOS.red;
const label2 = ({ theme }) => tokens.label2(theme);
const dot = (color) => <span style={{ width: 7, height: 7, borderRadius: "50%", background: color, opacity: 1 }} />;
const signedPercent = (v) => `${v > 0 ? "+" : ""}${(v || 0).toLocaleString("pt-BR", { maximumFractionDigits: 1 })}%`;

const Legend = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  font-size: 0.76em;
`;

/* Regime, trilho na escala comum e valor: as quatro linhas se comparam direto */
const Balance = styled.div`
  flex: 1;
  margin-top: 18px;
  display: flex;
  flex-direction: column;
  justify-content: space-evenly;
  gap: 22px;

  .regime { display: grid; grid-template-columns: 56px minmax(0, 1fr); gap: 12px; align-items: center; }
  .who { font-size: 1.12em; font-weight: 600; letter-spacing: -0.03em; }
  .rows { display: flex; flex-direction: column; gap: 12px; min-width: 0; }
  .row { display: grid; grid-template-columns: 64px minmax(0, 1fr) 100px; gap: 12px; align-items: center; }
  .k { font-size: 0.78em; color: ${label2}; }
  .track { display: flex; height: 18px; gap: 1px; }
  .track span { display: block; height: 100%; min-width: 1px; }
  .track span:first-child { border-radius: 4px 0 0 4px; }
  .track span:last-child { border-radius: 0 4px 4px 0; }
  .track span:only-child { border-radius: 4px; }
  .v { text-align: right; white-space: nowrap; font-size: 0.92em; font-weight: 600; letter-spacing: -0.025em; }
`;

const FlowHead = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) 84px 84px 100px;
  gap: 12px;
  margin-top: 14px;
  padding-bottom: 7px;
  font-size: 0.7em;
  color: ${({ theme }) => tokens.label3(theme)};
  border-bottom: 1px solid ${({ theme }) => hairline(theme)};
  > :not(:first-child) { text-align: right; }
`;

const FlowRow = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) 84px 84px 100px;
  gap: 12px;
  align-items: center;
  min-height: 50px;
  padding: 6px 0;
  border-bottom: 1px solid ${({ theme }) => hairline(theme)};
  &:last-child { border-bottom: none; }

  .name { min-width: 0; font-size: 0.84em; font-weight: 500; letter-spacing: -0.012em; line-height: 1.3; }
  .name small { display: block; margin-top: 2px; font-size: 0.86em; font-weight: 300; letter-spacing: 0; color: ${label2}; }
  .n { text-align: right; white-space: nowrap; font-size: 0.82em; color: ${label2}; }
  .end { text-align: right; white-space: nowrap; font-size: 0.92em; font-weight: 600; letter-spacing: -0.025em; }
  .note { grid-column: 2 / -1; text-align: right; font-size: 0.78em; color: ${label2}; }
`;

const NetBlock = styled.div`
  margin-top: 16px;
  .lab { font-size: 0.78em; color: ${label2}; }
  .big { margin-top: 3px; font-size: 1.72em; font-weight: 600; letter-spacing: -0.04em; line-height: 1.05; white-space: nowrap; }
  .big .cur { margin-right: 3px; font-size: 0.5em; font-weight: 500; letter-spacing: 0; opacity: 0.5; }
  .big .unit { margin-left: 3px; font-size: 0.5em; font-weight: 500; letter-spacing: 0; opacity: 0.5; }
  .big.past { font-size: 1.2em; color: ${label2}; }
  .link { margin: 12px 0 12px 3px; height: 22px; width: 1px; background: ${({ theme }) => hairline(theme, true)}; }
`;

const SegmentHead = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 10px;
  .chips { margin-top: 3px; font-size: 0.76em; color: ${label2}; }
`;

const SegmentFigure = styled.div`
  margin-top: 14px;
  .big { font-size: 1.72em; font-weight: 600; letter-spacing: -0.04em; line-height: 1.05; white-space: nowrap; }
  .big .sign { margin-right: 1px; font-weight: 500; }
  .big .cur { margin-right: 3px; font-size: 0.5em; font-weight: 500; letter-spacing: 0; opacity: 0.5; }
  .big .unit { margin-left: 3px; font-size: 0.5em; font-weight: 500; letter-spacing: 0; opacity: 0.5; }
  .cap { margin-top: 3px; font-size: 0.72em; color: ${({ theme }) => tokens.label3(theme)}; }
`;

const Facts = styled.dl`
  margin: 14px 0 0;
  display: grid;
  grid-template-columns: 1fr auto;
  font-size: 0.82em;
  dt, dd { margin: 0; padding: 8px 0; border-top: 1px solid ${({ theme }) => hairline(theme)}; }
  dt { color: ${label2}; }
  dd { font-weight: 600; letter-spacing: -0.02em; text-align: right; white-space: nowrap; }
`;

const Texts = styled.div`
  margin-top: auto;
  padding-top: 12px;
  font-size: 0.76em;
  font-weight: 300;
  line-height: 1.5;
  color: ${label2};
  p { margin: 0; }
  p + p { margin-top: 6px; }
  b { font-weight: 500; color: ${({ theme }) => theme.text}; }
`;

const Anatomy = styled.div`
  margin-top: 16px;
  .bar { display: flex; gap: 2px; height: 8px; border-radius: 4px; overflow: hidden; }
  .bar span { display: block; }
  .caps { display: flex; justify-content: space-between; gap: 16px; margin-top: 8px; font-size: 0.76em; color: ${label2}; }
  .caps span:last-child { text-align: right; }
  .caps b { font-weight: 600; color: ${({ theme }) => theme.text}; }
`;

const RateLine = styled.div`
  margin: 16px 0;
  display: grid;
  grid-template-columns: minmax(110px, 150px) minmax(0, 1fr);
  gap: 14px;
  align-items: center;
  .name { font-size: 0.84em; font-weight: 500; letter-spacing: -0.012em; }
  .name small { display: block; margin-top: 2px; font-size: 0.86em; font-weight: 300; letter-spacing: 0; color: ${label2}; }
`;

const WorkshopHead = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) 98px 98px;
  gap: 12px;
  margin-top: 14px;
  padding-bottom: 7px;
  font-size: 0.7em;
  color: ${({ theme }) => tokens.label3(theme)};
  border-bottom: 1px solid ${({ theme }) => hairline(theme)};
  > :not(:first-child) { text-align: right; }
`;

const WorkshopRow = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) 98px 98px;
  gap: 12px;
  align-items: center;
  min-height: 48px;
  padding: 5px 0;
  border-bottom: 1px solid ${({ theme }) => hairline(theme)};

  .name { min-width: 0; font-size: 0.84em; font-weight: 500; letter-spacing: -0.012em; line-height: 1.3; }
  .name small { display: block; margin-top: 2px; font-size: 0.86em; font-weight: 300; letter-spacing: 0; color: ${label2}; }
  .n { text-align: right; white-space: nowrap; font-size: 0.84em; color: ${label2}; }
  &.total { border-bottom: none; }
  &.total .n { font-size: 0.94em; font-weight: 600; letter-spacing: -0.025em; color: ${({ theme }) => theme.text}; }
`;

const Plan = styled.div`
  margin-top: 16px;
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0 28px;
  @media (max-width: 900px) { grid-template-columns: 1fr; gap: 18px; }

  .col > .h { display: flex; align-items: baseline; justify-content: space-between; gap: 8px; padding-bottom: 9px; font-size: 0.82em; font-weight: 600; letter-spacing: -0.012em; border-bottom: 2px solid var(--c); }
  .col > .h span { font-weight: 400; color: ${label2}; }
  .item { padding: 12px 0; border-bottom: 1px solid ${({ theme }) => hairline(theme)}; }
  .item:last-child { border-bottom: none; }
  .t { font-size: 0.88em; font-weight: 500; letter-spacing: -0.012em; line-height: 1.3; }
  .s { margin-top: 4px; font-size: 0.76em; font-weight: 300; line-height: 1.45; color: ${label2}; }
`;

const StressRow = styled(CardRow).attrs({ as: "div" })`
  cursor: default;
  grid-template-columns: 132px minmax(0, 1fr);
  &:hover { background: transparent; }
`;

function BalanceRow({ label, parts, value, max }) {
  return (
    <div className="row">
      <span className="k">{label}</span>
      <span className="track" role="img" aria-label={`${label}: ${formatCompact(value)}`}>
        {parts.filter((p) => p.value > 0).map((p) => (
          <span key={p.key} title={`${p.label}: ${formatCurrency(p.value)}`} style={{ width: `${(p.value / max) * 100}%`, background: p.color, opacity: p.o || 0.85 }} />
        ))}
      </span>
      <span className="v" title={formatCurrency(value)}><Num value={value} format={formatCompact} /></span>
    </div>
  );
}

function BalanceWidget({ data }) {
  const { debit, credit, net } = data;
  const max = Math.max(debit.today, debit.future, 1);
  const debitParts = (key) => debit.lines.filter((l) => !l.tag).map((l, i) => ({
    key: l.label, label: l.label, value: l[key], color: DEBIT, o: i === 0 ? 0.9 : 0.55,
  }));
  const creditParts = (key, netValue) => [
    ...credit.lines.map((l, i) => ({
      key: l.label, label: l.label, value: l[key] || 0, color: i === 2 ? IOS.mint : CREDIT, o: [0.9, 0.6, 1][i],
    })),
    { key: "pay", label: "A pagar", value: Math.max(0, netValue), color: PAY, o: 0.9 },
  ];
  return (
    <Widget $span={3} $delay={150} style={{ display: "flex", flexDirection: "column" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, flexWrap: "wrap" }}>
        <div style={{ minWidth: 0 }}>
          <div className="w-title">Volume bruto de caixa fiscal</div>
          <div className="w-sub">Hoje e 2033 na mesma escala, por mês. Na segunda barra, o crédito abate o débito, e o vermelho é o que sai do caixa</div>
        </div>
        <Legend>
          <Tag $color={DEBIT}>Débito</Tag>
          <Tag $color={CREDIT}>Crédito</Tag>
          <Tag $color={PAY}>A pagar</Tag>
        </Legend>
      </div>
      <Balance>
        <div className="regime">
          <div className="who">Hoje</div>
          <div className="rows">
            <BalanceRow label="Débito" parts={debitParts("atual")} value={debit.today} max={max} />
            <BalanceRow label="Crédito" parts={creditParts("atual", net.today)} value={credit.today} max={max} />
          </div>
        </div>
        <div className="regime">
          <div className="who">2033</div>
          <div className="rows">
            <BalanceRow label="Débito" parts={debitParts("futuro")} value={debit.future} max={max} />
            <BalanceRow label="Crédito" parts={creditParts("futuro", net.future)} value={credit.future} max={max} />
          </div>
        </div>
      </Balance>
    </Widget>
  );
}

const compactPieces = (v) => formatCompact(Math.abs(v || 0)).match(/R\$\s*([\d.,]+)\s*(.*)/) || [];
const compactNumber = (v) => compactPieces(v)[1] || "0";
const compactUnit = (v) => compactPieces(v)[2] || "";

export function MoneyParts({ value }) {
  return <><span className="cur">R$</span><Num value={value} format={compactNumber} /><Num className="unit" value={value} format={compactUnit} /></>;
}

function NetWidget({ net }) {
  return (
    <Widget $delay={200} style={{ display: "flex", flexDirection: "column" }}>
      <div className="w-title">Pagamento líquido</div>
      <div className="w-sub">Saída real para o governo, por mês</div>
      <NetBlock>
        <div className="lab">Regime atual</div>
        <div className="big past" title={formatCurrency(net.today)}><MoneyParts value={net.today} /></div>
        <div className="link" />
        <div className="lab">Regime futuro</div>
        <div className="big" title={formatCurrency(net.future)}><MoneyParts value={net.future} /></div>
      </NetBlock>
      <Facts style={{ marginTop: "auto" }}>
        <dt>Variação</dt>
        <dd style={{ color: directionColor(net.percent) }}><Num value={net.percent} format={signedPercent} /></dd>
        <dt>Diferença por mês</dt>
        <dd style={{ color: directionColor(net.future - net.today) }}><Num value={net.future - net.today} format={signed} /></dd>
      </Facts>
    </Widget>
  );
}

function FlowCard({ title, sub, side, delay, color }) {
  return (
    <Widget $span={2} $delay={delay}>
      <div className="w-title">{title}</div>
      <div className="w-sub">{sub}</div>
      <TopHeadline>
        <span className="n" style={{ color }} title={formatCurrency(side.future)}><Num value={side.future} format={formatCompact} /></span>
        <span className="l">por mês em 2033, <Num value={side.percent} format={signedPercent} /> sobre hoje</span>
      </TopHeadline>
      <FlowHead aria-hidden="true">
        <span>Item</span>
        <span>Atual</span>
        <span>Futuro</span>
        <span>Variação</span>
      </FlowHead>
      {side.lines.map((l) => {
        const [from, to] = l.label.split(" → ");
        const bracket = (to || from).match(/^(.*?)\s*\[(.*)\]$/);
        const short = (bracket ? bracket[1] : (to || from)).replace(/\s*\(NOVO benefício\)/, "");
        if (l.tag && l.atual === 0 && l.futuro === 0) {
          return (
            <FlowRow key={l.label}>
              <div className="name" style={{ fontWeight: 400 }}>{l.label.split(" — ")[0]}<small>{l.label.split(" — ")[1]}</small></div>
              <div className="note">{l.tag === "NÃO APLICA" ? "Não aplica" : "Aplica: verificar NCM"}</div>
            </FlowRow>
          );
        }
        return (
          <FlowRow key={l.label}>
            <div className="name">
              {short}
              <small>{to ? `Hoje: ${from}${bracket ? ` · ${bracket[2]}` : ""}` : l.tag}</small>
            </div>
            <div className="n" title={formatCurrency(l.atual)}><Num value={l.atual} format={formatCompact} /></div>
            <div className="n" title={formatCurrency(l.futuro)}><Num value={l.futuro} format={formatCompact} /></div>
            <div className="end" title={formatCurrency(l.variacao ?? l.futuro - l.atual)}><Num value={l.variacao ?? l.futuro - l.atual} format={signed} /></div>
          </FlowRow>
        );
      })}
    </Widget>
  );
}

function RiskWidget({ risks }) {
  const band = (m) => {
    if (m >= 5) return { color: IOS.red, label: "Exposição alta" };
    if (m >= 2) return { color: IOS.gray, label: "Exposição média" };
    return { color: IOS.green, label: "Exposição baixa" };
  };
  const titles = ["Crédito tributário por mês", "Débito bruto por mês", "Pagamento líquido por mês"];
  const subs = ["Máxima exposição", "Obrigação fiscal bruta", "Caixa real"];
  return (
    <Widget $span={2} $delay={250}>
      <div className="w-title">Risco de capital de giro</div>
      <div className="w-sub">Créditos em trânsito sob impugnação ou atraso levam a exposição junto. Um sistema fiscal robusto deixa de ser opcional</div>
      <div style={{ marginTop: 12 }}>
        {risks.map((r, i) => {
          const b = band(r.multiplier);
          return (
            <StressRow key={r.label}>
              <span className="metric"><Figure times={r.multiplier} /><span className="cap"><Tag $color={b.color} style={{ fontSize: "1em" }}>{b.label}</Tag></span></span>
              <span className="text">
                <span className="t">{titles[i]}</span>
                <span className="s" title={`${formatCurrency(r.today)} → ${formatCurrency(r.future)}`}><Num value={r.today} format={formatCompact} /> → <Num value={r.future} format={formatCompact} /> · {subs[i]}</span>
              </span>
            </StressRow>
          );
        })}
      </div>
    </Widget>
  );
}

function StressSlider({ label, value, min, max, step, format, onChange }) {
  const theme = useTheme();
  const position = ((value - min) / (max - min)) * 100;
  return (
    <Control style={{ "--p": `${position}%` }}>
      <span className="top">
        <span className="name">{label}</span>
        <span className="val"><Num value={value} format={format} /></span>
      </span>
      <span className="slider" style={{ display: "block", "--fill": tokens.label2(theme), "--rest": "rgba(118,118,128,0.28)" }}>
        <FluidRange min={min} max={max} step={step} value={value} format={format} aria-label={label} aria-valuetext={format(value)} onChange={onChange} />
      </span>
    </Control>
  );
}

function StressWidget({ credit, net }) {
  const [months, setMonths] = useState(1);
  const [disallowed, setDisallowed] = useState(1);
  const capital = credit.future * months;
  const capitalToday = credit.today * months;
  const cost = credit.future * (disallowed / 100);
  const costToday = credit.today * (disallowed / 100);
  return (
    <Widget $span={2} $delay={300} style={{ display: "flex", flexDirection: "column" }}>
      <div className="w-title">Teste de estresse do crédito</div>
      <div className="w-sub">E se o crédito atrasar ou for recusado? Cenário ilustrativo, que não altera os demais números</div>
      <div style={{ marginTop: 12, display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", columnGap: 24 }}>
        <StressSlider label="Atraso no crédito" value={months} min={0} max={6} step={1} format={(v) => `${v.toLocaleString("pt-BR", { maximumFractionDigits: 2 })} ${Math.abs(v - 1) < 0.005 ? "mês" : "meses"}`} onChange={setMonths} />
        <StressSlider label="Crédito glosado" value={disallowed} min={0} max={10} step={0.5} format={finePercent} onChange={setDisallowed} />
      </div>
      <div>
        <StressRow>
          <span className="metric"><Figure money={capital} plain /><span className="cap">capital de giro</span></span>
          <span className="text">
            <span className="t">Dinheiro parado à espera do crédito</span>
            <span className="s">Com o mesmo atraso, hoje seriam <Num value={capitalToday} format={formatCompact} />.</span>
          </span>
        </StressRow>
        <StressRow>
          <span className="metric"><Figure money={cost} color={PAY} /><span className="cap">a pagar, por mês</span></span>
          <span className="text">
            <span className="t">Custo do crédito recusado</span>
            <span className="s">O líquido iria de <Num value={net.future} format={formatCompact} /> para <Num value={net.future + cost} format={formatCompact} />. Hoje, a mesma glosa custaria <Num value={costToday} format={formatCompact} />.</span>
          </span>
        </StressRow>
      </div>
    </Widget>
  );
}

function SegmentCard({ segment: s, delay }) {
  const up = s.delta >= 0;
  return (
    <Widget $delay={delay} style={{ display: "flex", flexDirection: "column" }}>
      <SegmentHead>
        <div style={{ minWidth: 0 }}>
          <div className="w-title">{s.label}</div>
          <div className="chips">{s.share} · {s.context}</div>
        </div>
        {s.mostAffected && <Tag $color={IOS.red} style={{ fontSize: "0.76em" }}>Mais impactado</Tag>}
      </SegmentHead>
      <SegmentFigure>
        <div className="big" title={formatCurrency(s.delta)}>
          <span className="sign" style={{ color: directionColor(s.delta) }}>{up ? "+" : "−"}</span><MoneyParts value={s.delta} />
        </div>
        <div className="cap">variação por mês</div>
      </SegmentFigure>
      <div style={{ marginTop: 12 }}>
        <Compare today={s.today} future={s.future} color={directionColor(s.delta)} />
      </div>
      <Facts>
        <dt>Carga atual por mês</dt><dd title={formatCurrency(s.today)}><Num value={s.today} format={formatCompact} /></dd>
        <dt>Carga futura por mês</dt><dd title={formatCurrency(s.future)}><Num value={s.future} format={formatCompact} /></dd>
      </Facts>
      <Texts>
        <p><b>Atual.</b> {s.todayText}</p>
        <p><b>Futuro.</b> {s.futureText}</p>
      </Texts>
    </Widget>
  );
}

function WorkshopAnatomy({ w }) {
  const share = (v) => (w.revenue ? (v / w.revenue) * 100 : 0);
  const scale = Math.max(10, Math.ceil(Math.max(w.nominal, w.rateFuture, w.iss) / 5) * 5);
  return (
    <Widget $span={2} $delay={400} style={{ display: "flex", flexDirection: "column" }}>
      <div className="w-title">Serviços: por que são o ponto crítico</div>
      <div className="w-sub">
        O ISS de <Num value={w.iss} format={formatPercent} /> incide só sobre o preço do serviço. CBS+IBS de <Num value={w.nominal} format={formatPercent} /> incide sobre a receita bruta, e a folha de pagamento não gera crédito
      </div>
      <div style={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "space-evenly" }}>
      <Anatomy>
        <div className="bar" role="img" aria-label={`Mão de obra ${formatPercent(share(w.labor))} da receita, sem crédito; materiais ${formatPercent(share(w.materials))}, com crédito`}>
          <span style={{ width: `${share(w.labor)}%`, background: PAY, opacity: 0.8 }} />
          <span style={{ flex: 1, background: CREDIT, opacity: 0.8 }} />
        </div>
        <div className="caps">
          <span><b><Num value={share(w.labor)} format={formatPercent} /></b> mão de obra, sem crédito</span>
          <span><b><Num value={share(w.materials)} format={formatPercent} /></b> materiais, com crédito</span>
        </div>
      </Anatomy>
      <div>
        <RateLine>
          <div className="name">Alíquota nominal<small>ISS → CBS+IBS</small></div>
          <RateDumbbell from={w.iss} to={w.nominal} color={IOS.gray} scale={scale} />
        </RateLine>
        <RateLine>
          <div className="name">Taxa efetiva<small>Depois do crédito em materiais</small></div>
          <RateDumbbell from={w.rateToday} to={w.rateFuture} color={directionColor(w.rateFuture - w.rateToday)} scale={scale} />
        </RateLine>
      </div>
      </div>
      <PeriodStats>
        <div>
          <span className="v" title={formatCurrency(w.loadFuture)}><Num value={w.loadFuture} format={formatCompact} /></span>
          <span className="l">Carga futura por mês</span>
        </div>
        <div>
          <span className="v">{w.rateFuture.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%</span>
          <span className="l">Taxa efetiva · {w.rateToday.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}% atual</span>
        </div>
        <div>
          <span className="v" title={formatCurrency(w.increase)}><Num value={w.increase} format={signed} /></span>
          <span className="l">Aumento no período-base</span>
        </div>
      </PeriodStats>
    </Widget>
  );
}

function WorkshopTable({ w }) {
  const rows = [
    { item: "Receita mensal de serviços", obs: "10% do faturamento. Base do cálculo", today: w.revenue, future: w.revenue },
    { item: "Custo de mão de obra", obs: "~60% da receita. Folha não credita", today: w.labor, future: w.labor },
    { item: "Materiais", obs: "~40% da receita. Crédito integral de CBS/IBS", today: w.materials, future: w.materials },
    { item: "ISS atual / CBS+IBS futuro, bruto", obs: "Alíquota nominal sobe", today: w.issToday, future: w.grossFuture },
    { item: "(−) Crédito em materiais e insumos", obs: "Compras com nota fiscal", today: 0, future: -w.creditMaterials },
  ];
  return (
    <Widget $span={2} $delay={450}>
      <div className="w-title">Conta dos serviços, por mês</div>
      <div className="w-sub">Do faturamento de serviços à carga total</div>
      <WorkshopHead aria-hidden="true">
        <span>Item</span>
        <span>Regime atual</span>
        <span>Regime futuro</span>
      </WorkshopHead>
      {rows.map((r) => (
        <WorkshopRow key={r.item}>
          <div className="name">{r.item}<small>{r.obs}</small></div>
          <div className="n" title={formatCurrency(r.today)}><Num value={r.today} format={formatCompact} /></div>
          <div className="n" title={formatCurrency(r.future)}><Num value={r.future} format={formatCompact} /></div>
        </WorkshopRow>
      ))}
      <WorkshopRow className="total">
        <div className="name">Carga total mensal</div>
        <div className="n" title={formatCurrency(w.issToday)}><Num value={w.issToday} format={formatCompact} /></div>
        <div className="n" title={formatCurrency(w.loadFuture)} style={{ color: directionColor(w.loadFuture - w.issToday) }}><Num value={w.loadFuture} format={formatCompact} /></div>
      </WorkshopRow>
    </Widget>
  );
}

function ActionPlan({ actions }) {
  const columns = [
    { key: "URGENTE", label: "Urgente", color: IOS.red },
    { key: "CURTO PRAZO", label: "Curto prazo", color: IOS.blue },
    { key: "MÉDIO PRAZO", label: "Médio prazo", color: IOS.green },
  ];
  return (
    <Widget $delay={500}>
      <div className="w-title">Ações preventivas recomendadas</div>
      <div className="w-sub">O que fazer primeiro, por prazo</div>
      <Plan>
        {columns.map((c) => {
          const list = actions.filter((a) => a.prioridade === c.key);
          return (
            <div className="col" key={c.key} style={{ "--c": c.color }}>
              <div className="h">{c.label}<span>{list.length}</span></div>
              {list.map((a) => (
                <div className="item" key={a.acao}>
                  <div className="t">{a.acao}</div>
                  <div className="s">{a.justificativa}</div>
                </div>
              ))}
            </div>
          );
        })}
      </Plan>
    </Widget>
  );
}

export default function CashDashboard({ fiscalData, isIndustrial, companyFiltered }) {
  const { params } = useReformaParams();
  const data = useMemo(() => buildCash(fiscalData, { ...params, isIndustrial }), [fiscalData, params, isIndustrial]);
  const {
    debit, credit, net, risks, segments, workshop, actions,
  } = data;
  const worst = segments.find((s) => s.mostAffected);

  return (
    <>
      <Section $first>
        {companyFiltered && (
          <Notice style={{ marginTop: 0, marginBottom: 12 }}>
            <FiInfo />
            <span>Com empresa selecionada, o caixa mistura bases: PIS/COFINS é apurado na matriz, e as despesas (M105) são do projeto inteiro.</span>
          </Notice>
        )}
        <WidgetGrid>
          <Widget $delay={0}>
            <div className="w-head">{dot(DEBIT)} Débito bruto mensal</div>
            <div className="w-value"><MoneyValue value={debit.future} /></div>
            <div className="w-foot"><Num value={debit.multiplier} format={formatTimes} /> o atual, de <Num value={debit.today} format={formatCompact} /></div>
          </Widget>
          <Widget $delay={50}>
            <div className="w-head">{dot(CREDIT)} Crédito recuperado mensal</div>
            <div className="w-value"><MoneyValue value={credit.future} /></div>
            <div className="w-foot"><Num value={credit.multiplier} format={formatTimes} /> o atual, de <Num value={credit.today} format={formatCompact} /></div>
          </Widget>
          <Widget $delay={100}>
            <div className="w-head">{dot(PAY)} Pagamento líquido mensal</div>
            <div className="w-value"><MoneyValue value={net.future} /></div>
            <div className="w-foot"><span style={{ color: directionColor(net.percent) }}><Num value={net.percent} format={signedPercent} /></span>&nbsp;sobre <Num value={net.today} format={formatCompact} /> atual</div>
          </Widget>
          <Widget $delay={150}>
            <div className="w-head">{dot(worst ? IOS.red : IOS.gray)} Segmento mais impactado</div>
            <div className="w-value" style={{ fontSize: "1.3em", letterSpacing: "-0.02em" }}>{worst ? worst.label : "Nenhum"}</div>
            <div className="w-foot">{worst ? <><Num value={worst.delta} format={signed} /> por mês</> : "Sem aumento por segmento"}</div>
          </Widget>
        </WidgetGrid>
        <Notice>
          <FiInfo />
          <span>
            Mesmo com o pagamento líquido {net.future <= net.today ? "menor" : "maior"} no futuro (<Num value={net.percent} format={signedPercent} />), CBS e IBS movimentam volumes brutos <Num value={debit.multiplier} format={formatTimes} /> maiores. Isso exige sistema fiscal robusto e gestão ativa de créditos. Valores por mês: total ÷ 12.
          </span>
        </Notice>
      </Section>

      <Section $delay={80}>
        <WidgetGrid>
          <BalanceWidget data={data} />
          <NetWidget net={net} />
        </WidgetGrid>
      </Section>

      <Section $delay={120}>
        <WidgetGrid>
          <FlowCard title="Saída: débito bruto" sub="Impostos sobre a receita" side={debit} delay={200} />
          <FlowCard title="Entrada: crédito recuperado" sub="Compensação pelas compras e despesas" side={credit} delay={250} />
        </WidgetGrid>
      </Section>

      <Section $delay={160}>
        <WidgetGrid>
          <RiskWidget risks={risks} />
          <StressWidget credit={credit} net={net} />
        </WidgetGrid>
      </Section>

      <Section $delay={200}>
        <WidgetGrid $cols={3}>
          {segments.map((s, i) => <SegmentCard key={s.key} segment={s} delay={350 + i * 50} />)}
        </WidgetGrid>
        <Notice>
          <FiInfo />
          <span>Os segmentos são estimativas à parte, com os percentuais dos Parâmetros e sem o crédito sobre despesas. Por isso, não somam ao pagamento líquido.</span>
        </Notice>
      </Section>

      <Section $delay={240}>
        <WidgetGrid>
          <WorkshopAnatomy w={workshop} />
          <WorkshopTable w={workshop} />
        </WidgetGrid>
      </Section>

      <Section $delay={280}>
        <ActionPlan actions={actions} />
      </Section>
    </>
  );
}
